import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import styled, { css, ThemeProps } from 'styled-components';
import { CardContent, CardHeader, CardImage } from '../Card';
import { Column, Row } from '../Grid';
import { Title } from '../Title';
import { TypoImage } from '../TypoImage';
import {
  withBreakpoints,
  WithBreakpointsProps
} from '../../provider/BreakpointProvider/withBreakpoints';
import { GridTeaserPage } from '../../provider/Typo3Provider/types';
import { Theme } from '../../theme';
import { media } from '../../theme/base/mixins';
import { StyledComponent } from '../../types';
import { rgba } from '../utils/rgba';
import { HtmlContent } from './HtmlContent';

interface MagazineTeaserProps {
  top?: boolean;
}

type MagazineTeaserPropsThemed = MagazineTeaserProps & ThemeProps<Theme>;

const MagazineTeaser: StyledComponent<
  MagazineTeaserProps,
  HTMLDivElement
> = styled.div`
  display: block;
  border: 1px solid ${(props: MagazineTeaserPropsThemed) =>
    props.theme.colors.gray};
  border-radius: 2px;
  background-color: ${(props: MagazineTeaserPropsThemed) =>
    props.theme.colors.white};
  box-shadow: 3px 3px 3px 0 ${(props: MagazineTeaserPropsThemed) =>
    rgba(props.theme.colors.black, 0.1)};
  height: calc(100% - 20px);
  margin-bottom: 20px;

  a {
    text-decoration: none;
  }

  ${CardImage} {
    height: ${(props: MagazineTeaserPropsThemed) =>
      !props.top ? '244px' : '500px'};
    overflow: hidden;

    ${(props: MagazineTeaserPropsThemed) =>
      props.top &&
      css`
        margin-right: ${props.theme.grid.columnGap / 2}px;
      `}

    img {
      position: relative;
      object-fit: cover;
      height: 100%;
      width: 100%;
    }
  }

  ${CardContent} {
    ${(props: MagazineTeaserPropsThemed) =>
      props.top &&
      css`
        display: flex;
        height: 100%;
      `}
  }

  ${CardImage} + ${CardContent} {
    margin: -50px 20px 0 20px;
    background-color: ${(props: MagazineTeaserPropsThemed) =>
      props.theme.colors.white};
  }

  ${CardHeader} {
    font-size: 1.8rem;
    font-weight: 300;

    ${Title.Card} {
      font-weight: 800;
      color: ${(props: MagazineTeaserPropsThemed) =>
        props.theme.colors.secondary};
    }

    ${(props: MagazineTeaserPropsThemed) =>
      props.top &&
      css`
        align-self: center;
        font-size: 1.8rem;
        line-height: 1.25;

        ${media.large`
        font-size: 2.4rem;
      `}
      `}
  }
`;

export interface MagazinPagesProps {
  pages: GridTeaserPage[];
}

const MagazinPagesWithBreakpoints = ({
  pages,
  breakpoints
}: MagazinPagesProps & WithBreakpointsProps) => {
  const topTeaser = pages.filter((item, key) => key === 0);

  const defaultTeaser = pages.filter((item, key) => key > 0);

  const { large } = breakpoints;

  return (
    <Fragment>
      {topTeaser.map(teaser => {
        return (
          <Column small={12} key={teaser.uid}>
            {large ? (
              <MagazineTeaser top>
                <Link to={teaser.link || ''}>
                  <Row noGap>
                    {teaser.magazine_teaser_top && (
                      <Column large={8}>
                        <CardImage>
                          <TypoImage image={teaser.magazine_teaser_top} />
                        </CardImage>
                      </Column>
                    )}
                    <Column large={4}>
                      <CardContent>
                        <CardHeader>
                          {teaser.title && (
                            <Title.Card>
                              <HtmlContent htmlString={teaser.title} />
                            </Title.Card>
                          )}
                          {teaser.subtitle && (
                            <HtmlContent htmlString={teaser.subtitle} />
                          )}
                        </CardHeader>
                      </CardContent>
                    </Column>
                  </Row>
                </Link>
              </MagazineTeaser>
            ) : (
              <MagazineTeaser>
                <Link to={teaser.link || ''}>
                  {teaser.magazine_teaser && (
                    <CardImage>
                      <TypoImage image={teaser.magazine_teaser} />
                    </CardImage>
                  )}
                  <CardContent>
                    <CardHeader>
                      {teaser.title && (
                        <Title.Card>
                          <HtmlContent htmlString={teaser.title} />
                        </Title.Card>
                      )}
                      {teaser.subtitle && (
                        <HtmlContent htmlString={teaser.subtitle} />
                      )}
                    </CardHeader>
                  </CardContent>
                </Link>
              </MagazineTeaser>
            )}
          </Column>
        );
      })}

      {defaultTeaser.map(teaser => {
        return (
          <Column large={4} medium={6} small={12} key={teaser.uid}>
            <MagazineTeaser>
              <Link to={teaser.link || ''}>
                {teaser.magazine_teaser && (
                  <CardImage>
                    <TypoImage image={teaser.magazine_teaser} />
                  </CardImage>
                )}
                <CardContent>
                  <CardHeader>
                    {teaser.title && (
                      <Title.Card>
                        <HtmlContent htmlString={teaser.title} />
                      </Title.Card>
                    )}
                    {teaser.subtitle && (
                      <HtmlContent htmlString={teaser.subtitle} />
                    )}
                  </CardHeader>
                </CardContent>
              </Link>
            </MagazineTeaser>
          </Column>
        );
      })}
    </Fragment>
  );
};

export const MagazinPages = withBreakpoints(MagazinPagesWithBreakpoints);
